import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { BrandLockup } from "@/shell/CandidateFlow";
import { NotificationBell } from "@/components/NotificationBell";

interface HomeShellProps {
  children: ReactNode;
  agencyName?: string;
  logoUrl?: string;
  logoFilter?: string;
  activeTab?: "home" | "chat" | "kb" | "settings" | "briefs";
  candidateInitials?: string;
}

function NavLink({
  href,
  active,
  children,
}: {
  href: string;
  active?: boolean;
  children: ReactNode;
}) {
  return (
    <Link
      to={href}
      className="px-3.5 py-1.5 rounded-md text-[13.5px] font-medium transition-colors"
      style={{
        color: active ? "var(--slate-700)" : "var(--slate-700)",
        background: active ? "var(--accent-50)" : "transparent",
      }}
    >
      {children}
    </Link>
  );
}

function HomeIcon() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="currentColor">
      <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
    </svg>
  );
}

function KbIcon() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
    </svg>
  );
}

function BriefsIcon() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="currentColor">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zm-1 7V3.5L18.5 9zM8 13h8v2H8zm0 4h8v2H8z" />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
    </svg>
  );
}

export function HomeShell({
  children,
  agencyName,
  logoUrl,
  logoFilter,
  activeTab = "home",
  candidateInitials = "",
}: HomeShellProps) {
  const { token } = useParams<{ token: string }>();

  return (
    <div className="min-h-screen" style={{ background: "var(--paper)", ["--tab-bar-h" as string]: "60px" }}>
      <header
        style={{ background: "var(--cream)", borderBottom: "1px solid var(--hair)" }}
        className="flex items-center justify-start min-h-[60px] px-4 md:px-8 py-2 gap-4 md:gap-6"
      >
        <Link
          to={`/invite/${token}/home`}
          className="shrink-0"
          style={{ textDecoration: "none" }}
          aria-label="Go to home"
        >
          {/* Same desktop/mobile split as RecruiterShell's header lockup —
              inline's mobile (flex-col) rendering stretches a wide logo
              <img> to its max-w cap and then object-contain centers the
              visible pixels inside that box, shifting it right of "Powered
              by" beneath it (confirmed via getBoundingClientRect on a real
              wordmark logo). The non-inline align="left" branch never
              relies on that stretch, so it doesn't have the bug. */}
          <div className="hidden md:flex">
            <BrandLockup agencyName={agencyName} logoUrl={logoUrl} logoFilter={logoFilter} inline />
          </div>
          <div className="flex md:hidden">
            <BrandLockup agencyName={agencyName} logoUrl={logoUrl} logoFilter={logoFilter} align="left" />
          </div>
        </Link>

        {/* Desktop nav — hidden on mobile */}
        <nav className="hidden md:flex gap-1 ml-auto shrink-0 items-center">
          <NavLink href={`/invite/${token}/home`} active={activeTab === "home"}>
            Home
          </NavLink>
          <NavLink href={`/invite/${token}/kb`} active={activeTab === "kb"}>
            Knowledge Base
          </NavLink>
          <NavLink href={`/invite/${token}/briefs`} active={activeTab === "briefs"}>
            Your Briefs
          </NavLink>
          <NotificationBell mode="candidate" />
          <details className="relative ml-1">
            <summary
              className="w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-bold list-none cursor-pointer"
              style={{ background: "var(--accent)", color: "var(--cream)" }}
            >
              {candidateInitials || "?"}
            </summary>
            <div
              className="absolute right-0 mt-2 py-1.5 rounded-xl z-10"
              style={{ background: "var(--cream)", border: "1px solid var(--hair)", boxShadow: "0 8px 24px rgba(27,26,36,0.12)", minWidth: 140 }}
            >
              <Link
                to={`/invite/${token}/settings`}
                className="block px-4 py-2 text-[13.5px] font-medium"
                style={{ color: "var(--slate-700)" }}
              >
                Settings
              </Link>
              <a
                href="/candidate/logout"
                className="block px-4 py-2 text-[13.5px] font-medium"
                style={{ color: "var(--slate-500)" }}
              >
                Log out
              </a>
            </div>
          </details>
        </nav>
      </header>

      {/* Extra bottom padding on mobile so content clears the fixed tab bar */}
      <main className="max-w-[1160px] mx-auto px-4 md:px-8 py-6 md:py-8 pb-24 md:pb-16">
        {children}
      </main>

      {/* Mobile bottom tab bar — visible below md breakpoint only */}
      <nav
        className="fixed bottom-0 left-0 right-0 flex items-center md:hidden border-t z-50"
        style={{
          background: "var(--cream)",
          borderColor: "var(--hair)",
          height: "calc(var(--tab-bar-h) + env(safe-area-inset-bottom, 0px))",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}
      >
        <Link
          to={`/invite/${token}/home`}
          className="flex-1 flex flex-col items-center gap-1 py-2.5 transition-colors"
          style={{ color: activeTab === "home" ? "var(--accent)" : "var(--slate-500)" }}
        >
          <HomeIcon />
          <span className="text-[9px] font-bold uppercase tracking-[0.05em]">Home</span>
        </Link>
        <Link
          to={`/invite/${token}/kb`}
          className="flex-1 flex flex-col items-center gap-1 py-2.5 transition-colors"
          style={{ color: activeTab === "kb" ? "var(--accent)" : "var(--slate-500)" }}
        >
          <KbIcon />
          <span className="text-[9px] font-bold uppercase tracking-[0.05em]">KB</span>
        </Link>
        {/* Your Briefs is in the desktop nav, which is display:none under md.
            Without this tab the screen was unreachable on a phone. */}
        <Link
          to={`/invite/${token}/briefs`}
          className="flex-1 flex flex-col items-center gap-1 py-2.5 transition-colors"
          style={{ color: activeTab === "briefs" ? "var(--accent)" : "var(--slate-500)" }}
        >
          <BriefsIcon />
          <span className="text-[9px] font-bold uppercase tracking-[0.05em]">Briefs</span>
        </Link>
        <Link
          to={`/invite/${token}/settings`}
          className="flex-1 flex flex-col items-center gap-1 py-2.5 transition-colors"
          style={{ color: activeTab === "settings" ? "var(--accent)" : "var(--slate-500)" }}
        >
          <SettingsIcon />
          <span className="text-[9px] font-bold uppercase tracking-[0.05em]">Settings</span>
        </Link>
      </nav>
    </div>
  );
}
